import React from 'react'
import { View, Text } from '@react-pdf/renderer'
import { styles } from '../styles'
import { PDFSummaryProps } from '../types'
import { 
  calculateTotalDistance, 
  calculateTotalElevation, 
  calculateNutritionTotals,
  formatNutritionRate,
  formatTime
} from '../utils'

export function PDFSummary({ run, compact = false }: PDFSummaryProps) {
  const segments = run.segments || []
  const aidStations = run.aid_stations || []
  const planItems = run.plan_items || []
  
  // Calculate totals
  const totalDistance = calculateTotalDistance(segments)
  const { gain: totalElevGain, loss: totalElevLoss } = calculateTotalElevation(segments)
  const totalNutrition = calculateNutritionTotals(planItems)
  
  // Calculate total time
  let cumulativeTime = 0
  segments.forEach(segment => {
    // This is a simplified pace calculation - in real implementation, use the actual pace calculation
    const estimatedPace = 8 // min/km default - should use actual calculation
    const segmentTime = (estimatedPace * segment.distance_km) + (segment.planned_stop_min || 0)
    cumulativeTime += segmentTime
  })
  
  const totalTimeHours = cumulativeTime / 60
  
  if (compact) {
    // Ultra-compact summary for race card
    return (
      <View style={styles.footerCompact}>
        <View style={[styles.flexRow, { justifyContent: 'space-between' }]}>
          <Text style={[styles.textBold, { fontSize: 11 }]}>
            {totalDistance.toFixed(1)}km
          </Text>
          <Text style={[styles.textBold, { fontSize: 11 }]}>
            Target: {formatTime(cumulativeTime)}
          </Text>
          <Text style={[styles.textBold, { fontSize: 11 }]}>
            +{totalElevGain}m
          </Text>
        </View>
        <View style={[styles.flexRow, { justifyContent: 'center', marginTop: 4 }]}>
          <Text style={{ fontSize: 8, color: styles.textSecondary.color }}>
            Carbs: {totalNutrition.carbs_g}g • Fluids: {totalNutrition.fluids_ml}ml • Sodium: {totalNutrition.sodium_mg}mg
          </Text>
        </View>
      </View>
    )
  }
  
  return (
    <View style={styles.summarySection}>
      {/* Main Statistics */}
      <View style={styles.summaryStats}>
        <View style={styles.statItem}>
          <Text style={styles.statLabel}>Total Distance</Text>
          <Text style={styles.statValue}>{totalDistance.toFixed(1)} km</Text>
        </View>
        <View style={styles.statItem}>
          <Text style={styles.statLabel}>Estimated Finish</Text>
          <Text style={styles.statValue}>{formatTime(cumulativeTime)}</Text>
        </View>
        <View style={styles.statItem}>
          <Text style={styles.statLabel}>Total Elevation</Text>
          <Text style={styles.statValue}>
            +{totalElevGain}m / -{totalElevLoss}m
          </Text>
        </View>
        <View style={styles.statItem}>
          <Text style={styles.statLabel}>Aid Stations</Text>
          <Text style={styles.statValue}>{aidStations.length} stations</Text>
        </View>
      </View>
      
      {/* Nutrition Summary */}
      <View style={styles.nutritionSummary}>
        <Text style={styles.nutritionTitle}>Nutrition Plan Summary</Text>
        <View style={styles.nutritionStats}>
          <View style={styles.statItem}>
            <Text style={styles.statLabel}>Carbs Target</Text>
            <Text style={styles.statValue}>
              {totalNutrition.carbs_g}g ({formatNutritionRate(totalNutrition.carbs_g, totalTimeHours, 'g')})
            </Text>
          </View>
          <View style={styles.statItem}>
            <Text style={styles.statLabel}>Fluids Target</Text>
            <Text style={styles.statValue}>
              {totalNutrition.fluids_ml}ml ({formatNutritionRate(totalNutrition.fluids_ml, totalTimeHours, 'ml')})
            </Text>
          </View>
          <View style={styles.statItem}>
            <Text style={styles.statLabel}>Sodium</Text>
            <Text style={styles.statValue}>
              {totalNutrition.sodium_mg}mg ({formatNutritionRate(totalNutrition.sodium_mg, totalTimeHours, 'mg')})
            </Text>
          </View>
          <View style={styles.statItem}>
            <Text style={styles.statLabel}>Caffeine</Text>
            <Text style={styles.statValue}>{totalNutrition.caffeine_mg}mg</Text>
          </View>
        </View>
      </View>
      
      {/* Run Notes if present */}
      {run.notes && (
        <View style={styles.notes}>
          <Text style={styles.notesTitle}>Run Notes</Text>
          <Text style={styles.notesText}>{run.notes}</Text>
        </View>
      )}
    </View>
  )
}